import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { useTabParam } from "../../hooks/useTabParam";
import DropdownSelect from "../../components/DropdownSelect";
import { EmployeeSearchMulti } from "../../components/EmployeeSearch";
import { Circle, CircleCheck, CircleX, Hourglass } from "lucide-react";

interface Course { id: number; title: string; description: string | null; provider: string; category: string; is_mandatory: boolean; renewal_period_months: number | null; is_active: boolean; }
interface Expiring { training_id: number; employee_name: string | null; course_title: string | null; expires_at: string | null; }

const TRAINING_TABS = ["courses", "compliance", "expiring"] as const;

export default function AdminTrainingPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [compliance, setCompliance] = useState<any>(null);
  const [expiring, setExpiring] = useState<Expiring[]>([]);
  const [tab, setTab] = useTabParam(TRAINING_TABS, "courses");
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState<Course | null>(null);
  const [assignCourseId, setAssignCourseId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, comp, exp] = await Promise.all([
        api.get<Course[]>("/api/training/courses"),
        api.get("/api/training/compliance").catch(() => null),
        api.get<Expiring[]>("/api/training/expiring?days=30").catch(() => []),
      ]);
      setCourses(c); setCompliance(comp); setExpiring(exp);
    } catch {}
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Training & Compliance</h1>
        <button type="button" onClick={() => { setEditCourse(null); setShowForm(true); }} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">+ Add Course</button>
      </div>

      <nav className="flex gap-1 border-b border-gray-200">
        <TabBtn active={tab === "courses"} onClick={() => setTab("courses")}>Courses ({courses.length})</TabBtn>
        <TabBtn active={tab === "compliance"} onClick={() => setTab("compliance")}>Compliance</TabBtn>
        <TabBtn active={tab === "expiring"} onClick={() => setTab("expiring")}>Expiring ({expiring.length})</TabBtn>
      </nav>

      {showForm && (
        <CourseForm
          course={editCourse}
          onSaved={() => { setShowForm(false); setEditCourse(null); void load(); }}
          onCancel={() => { setShowForm(false); setEditCourse(null); }}
        />
      )}

      {assignCourseId != null && (
        <AssignModal courseId={assignCourseId} onClose={() => { setAssignCourseId(null); void load(); }} />
      )}

      {tab === "courses" && (
        <div className="space-y-2">
          {courses.map((c) => (
            <div key={c.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{c.title}</p>
                  <p className="text-xs text-gray-500">{c.category} · {c.provider}{c.is_mandatory ? " · Mandatory" : ""}{c.renewal_period_months ? ` · Renews every ${c.renewal_period_months}mo` : ""}</p>
                  {c.description && <p className="mt-1 text-xs text-gray-600">{c.description}</p>}
                </div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setAssignCourseId(c.id)} className="rounded border border-gray-300 px-2 py-1 text-xs">Assign</button>
                  <button type="button" onClick={() => { setEditCourse(c); setShowForm(true); }} className="rounded border border-gray-300 px-2 py-1 text-xs">Edit</button>
                </div>
              </div>
            </div>
          ))}
          {courses.length === 0 && !showForm && <p className="text-sm text-gray-400">No courses yet.</p>}
        </div>
      )}

      {tab === "compliance" && compliance && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Employee</th>
                {(compliance.courses ?? []).map((c: any) => <th key={c.id} className="px-3 py-2 text-center" title={c.title}>{c.title.slice(0, 12)}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(compliance.matrix ?? []).map((row: any) => (
                <tr key={row.employee_id}>
                  <td className="px-3 py-2 font-medium text-gray-900">{row.employee_name}</td>
                  {(compliance.courses ?? []).map((c: any) => {
                    const s = row.courses[c.id];
                    const icon =
                      s === "completed" ? <CircleCheck size={18} strokeWidth={1.75} className="text-green-600" aria-label="Completed" />
                      : s === "in_progress" ? <Hourglass size={18} strokeWidth={1.75} className="text-amber-600" aria-label="In progress" />
                      : s === "expired" ? <CircleX size={18} strokeWidth={1.75} className="text-red-600" aria-label="Expired" />
                      : s === "not_started" ? <Circle size={18} strokeWidth={1.75} className="text-gray-300" aria-label="Not started" />
                      : <span className="text-gray-300">—</span>;
                    return <td key={c.id} className="px-3 py-2"><span className="flex justify-center">{icon}</span></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "expiring" && expiring.map((e) => (
        <div key={e.training_id} className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-gray-900">{e.employee_name} — {e.course_title}</p>
          <p className="text-xs text-amber-800">Expires: {e.expires_at}</p>
        </div>
      ))}
    </div>
  );
}

function CourseForm({ course, onSaved, onCancel }: { course: Course | null; onSaved: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState(course?.title ?? "");
  const [description, setDescription] = useState(course?.description ?? "");
  const [provider, setProvider] = useState(course?.provider ?? "internal");
  const [category, setCategory] = useState(course?.category ?? "other");
  const [mandatory, setMandatory] = useState(course?.is_mandatory ?? false);
  const [renewal, setRenewal] = useState<string>(course?.renewal_period_months?.toString() ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSubmitting(true); setError(null);
    const payload = { title, description: description || null, provider, category, is_mandatory: mandatory, renewal_period_months: renewal ? Number(renewal) : null };
    try {
      if (course) { await api.put(`/api/training/courses/${course.id}`, payload); }
      else { await api.post("/api/training/courses", payload); }
      onSaved();
    } catch (err) { setError((err as Error).message); } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">{course ? "Edit Course" : "New Course"}</h3>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required className="input mt-1" />
      </label>
      <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Description</span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="input mt-1" />
      </label>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Provider</span>
          <DropdownSelect listCode="training_provider" value={provider} onChange={setProvider} className="input mt-1" />
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Category</span>
          <DropdownSelect listCode="training_category" value={category} onChange={setCategory} className="input mt-1" />
        </label>
        <label className="block text-sm"><span className="text-xs font-medium text-gray-700">Renewal (months)</span>
          <input type="number" min={0} value={renewal} onChange={(e) => setRenewal(e.target.value)} placeholder="e.g. 12" className="input mt-1" />
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} /> Mandatory</label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{submitting ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

function AssignModal({ courseId, onClose }: { courseId: number; onClose: () => void }) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const selectedArr = useMemo(() => [...selected], [selected]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const assign = async () => {
    setSubmitting(true);
    try {
      const res = await api.post<{ assigned: number }>(`/api/training/courses/${courseId}/assign`, { employee_ids: Array.from(selected) });
      setResult(`${res.assigned} assigned`);
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 md:items-center md:px-4" onClick={onClose}>
      <div className="w-full rounded-t-xl bg-white p-5 shadow-2xl md:max-w-lg md:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900">Assign Training</h3>
        {result ? (
          <div className="mt-3">
            <p className="text-sm text-green-600">{result}</p>
            <button type="button" onClick={onClose} className="mt-3 rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white">Done</button>
          </div>
        ) : (
          <>
            <div className="mt-3">
              <EmployeeSearchMulti
                values={selectedArr}
                onChange={(ids) => setSelected(new Set(ids))}
                placeholder="Search employees to assign..."
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={assign} disabled={submitting || selected.size === 0} className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {submitting ? "Assigning..." : `Assign (${selected.size})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" onClick={onClick} className={["px-4 py-2 text-sm font-semibold", active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500"].join(" ")}>{children}</button>;
}
